<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable><k-card>
	<k-card-title class="deep-purple lighten-4 pt-2">
		<k-icon large color="deep-purple darken-4" class="mr-3">fas fa-certificate</k-icon>
		<h3 class="deep-purple--text text--darken-4">Best Practices Review</h3>
		<k-spacer></k-spacer>
		<!-- <k-btn class="mr-3 k-tight-btn" small color="secondary" @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-code</k-icon>Download Framework</k-btn> -->
		<k-btn v-if="show_refresh_btn" class="mr-3 k-tight-btn" small color="secondary" @click="load_bp_data('force_update')"><k-icon small class="mr-2">fas fa-recycle</k-icon>Refresh Report Data</k-btn>
		<!-- <k-btn class="mr-3 k-tight-btn" small color="secondary" @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-list</k-icon>Download Report</k-btn> -->
		<k-btn class="mr-2 k-tight-btn" small color="secondary" @click="copy_report_link"><k-icon small class="mr-2">fas fa-link</k-icon>Copy Report Link</k-btn>
		<k-btn class="mr-4 k-tight-btn" small color="secondary" @click="print_report"><k-icon small class="mr-2">fas fa-print</k-icon>Print</k-btn>
		<k-btn color="secondary" dark small fab accessible-label="Close best practices review" @click="$emit('dialog_cancel')"><k-icon>fas fa-xmark</k-icon></k-btn>
	</k-card-title>
	<k-card-text id="k-bpr-text" class="pt-3" style="font-size:16px">
		<!-- this will only appear on print -->
		<div class="k-bpr-print-header">
			<h2>Best Practices Review</h2>
			<h3 class="py-1">{{framework_record.json.CFDocument.title}}</h3>
			<ul class="py-1">
				<li>Framework identifier: {{framework_record.json.CFDocument.identifier}}</li>
				<li>Framework creator: {{framework_record.json.CFDocument.creator}}</li>
			</ul>
		</div>

		<h2 class="k-bp-hide-from-print">{{framework_record.json.CFDocument.title}}</h2>
		<div class="k-bpr-intro k-bp-hide-from-print">
			This report summarizes this framework’s conformance with CASE<sup>®</sup> 1.1 best practices.
			<k-btn v-if="show_open_framework_btn" x-small class="k-tight-btn" text color="#333" dark @click="open_framework"><k-icon small class="mr-2">fas fa-map</k-icon> Open Framework</k-btn>
		</div>

		<div v-if="rd">
			<!-- SUMMARY -->
			<div class="k-bpr-section">
				<div class="k-bpr-section-title">
					<div v-html="severity_indicator(rd.summary.bp_score)"></div>
					<!-- <div :class="`${rd.summary.total_issue_count ? 'red' : 'green'}--text text--darken-3 ml-2`"> -->
					<div class="ml-2">
						<span v-if="rd.summary.bp_issue_type_count>0">{{rd.summary.bp_issue_type_count}} {{U.ps(' Issue Type', rd.summary.bp_issue_type_count)}} / </span>
						{{rd.summary.total_issue_count}} {{U.ps(' Total Issue', rd.summary.total_issue_count)}}
					</div>
					<k-spacer/>
				</div>
			</div>

			<div style="border-bottom:1px solid #000; margin-top:12px;"></div>

			<!-- CFDocument -->
			<div class="k-bpr-section" :class="rd.CFDocument==last_clicked_item?'k-bpr-item-last-clicked':''">
				<div class="k-bpr-section-title">
					<div v-html="severity_indicator(rd.CFDocument.bp_score)"></div>
					<div class="ml-2">CFDocument / CFFramework</div>
					<div class="k-bpr-section-issue-counts" :class="rd.CFDocument.total_issue_count?'':'green--text text--darken-3'">
						{{rd.CFDocument.total_issue_count + U.ps(' issue', rd.CFDocument.total_issue_count)}}
					</div>
					<k-btn v-if="rd.CFDocument.total_issue_count" x-small text class="k-tight-btn ml-1 k-bp-hide-from-print" color="primary" @click.stop="view_json(rd.CFDocument)"><k-icon x-small class="mr-1">fas fa-code</k-icon> JSON</k-btn>
					<k-spacer/>
				</div>
				<k-expand-transition><ul v-if="rd.CFDocument.total_issue_count" v-show="rd.CFDocument.section_showing" class="k-bpr-section-issues">
					<li v-for="(count, issue_code) in rd.CFDocument.issue_counts" :key="issue_code" v-html="issue_html(issue_code, -1, 'D')"></li>
				</ul></k-expand-transition>
			</div>

			<!-- CFItems -->
			<div class="k-bpr-section">
				<div class="k-bpr-section-title">
					<div v-html="severity_indicator(rd.CFItems.bp_score)"></div>
					<div class="ml-2">CFItems</div>
					<div class="k-bpr-section-issue-counts" :class="rd.CFItems.total_issue_count?'':'green--text text--darken-3'">
						<span class="black--text">{{framework_record.json.CFItems.length}} total items;</span>
						{{rd.CFItems.total_issue_count + U.ps(' issue', rd.CFItems.total_issue_count)}}
						<span v-if="rd.CFItems.total_issue_count>0">
							<span style="color:#000">in</span>
							{{rd.CFItems.item_count + U.ps(' item', rd.CFItems.item_count)}}
							({{(rd.CFItems.item_count / framework_record.json.CFItems.length * 100).toFixed(1)}}%)
						</span>
					</div>
					<k-spacer/>
				</div>

				<ul v-if="rd.CFItems.total_issue_count>0" class="k-bpr-section-issues">
					<li v-for="(count, issue_code) in rd.CFItems.issue_counts" :key="issue_code" v-html="issue_html(issue_code, count, 'I')"></li>
				</ul>

				<div v-if="rd.CFItems.total_issue_count > 0" class="mb-2 k-bp-hide-from-print">
					<k-btn small text color="deep-purple" class="k-nocaps-btn k-tight-btn" @click="rd.CFItems.section_showing=!rd.CFItems.section_showing">
						<k-icon class="mr-2">{{section_icon(rd.CFItems)}}</k-icon>
						{{rd.CFItems.section_showing?'Hide':'Show'}} Individual CFItems
					</k-btn>
				</div>

				<k-expand-transition><div v-if="rd.CFItems.total_issue_count > 0 && rd.CFItems.section_showing">
					<div v-for="(item, identifier, index) in rd.CFItems.items" :key="identifier" class="k-bpr-item" :class="item==last_clicked_item?'k-bpr-item-last-clicked':''">
						<div class="d-flex align-center">
							<b>{{(index+1)}}.  </b>
							<div class="k-bpr-item-display" v-html="item.item_display"></div>
							<k-btn x-small text class="k-tight-btn ml-1 k-bp-hide-from-print" color="deep-purple" @click.stop="view_json(item)"><k-icon x-small class="mr-1">fas fa-code</k-icon> JSON</k-btn>
							<k-btn x-small text class="k-tight-btn ml-1 k-bp-hide-from-print" color="deep-purple" @click.stop="view_framework(item)"><k-icon x-small class="mr-1">fas fa-map</k-icon> View in Framework</k-btn>
							<k-spacer/>
						</div>
						<ul class="k-bpr-section-issues">
							<li v-for="(count, issue_code) in item.issue_counts" :key="issue_code" v-html="issue_html(issue_code, -1, 'I')"></li>
						</ul>
					</div>
				</div></k-expand-transition>
			</div>

			<!-- CFAssociations -->
			<div class="k-bpr-section">
				<div class="k-bpr-section-title">
					<div v-html="severity_indicator(rd.CFAssociations.bp_score)"></div>
					<div class="ml-2">CFAssociations</div>
					<div class="k-bpr-section-issue-counts" :class="rd.CFAssociations.total_issue_count?'':'green--text text--darken-3'">
						<span class="black--text">{{framework_record.json.CFAssociations.length}} total associations;</span>
						{{rd.CFAssociations.total_issue_count + U.ps(' issue', rd.CFAssociations.total_issue_count)}}
						<span v-if="rd.CFAssociations.total_issue_count>0">
							<span style="color:#000">in</span>
							{{rd.CFAssociations.item_count + U.ps(' association', rd.CFAssociations.item_count)}}
							({{(rd.CFAssociations.item_count / framework_record.json.CFAssociations.length * 100).toFixed(1)}}%)
						</span>
					</div>
					<k-spacer/>
				</div>

				<ul v-if="rd.CFAssociations.total_issue_count>0" class="k-bpr-section-issues">
					<li v-for="(count, issue_code) in rd.CFAssociations.issue_counts" :key="issue_code" v-html="issue_html(issue_code, count, 'A')"></li>
				</ul>

				<div v-if="rd.CFAssociations.total_issue_count > 0" class="mb-2">
					<k-btn small text color="deep-purple" class="k-nocaps-btn k-tight-btn" @click="rd.CFAssociations.section_showing=!rd.CFAssociations.section_showing">
						<k-icon class="mr-2">{{section_icon(rd.CFAssociations)}}</k-icon>
						{{rd.CFAssociations.section_showing?'Hide':'Show'}} Individual CFAssociations
					</k-btn>
				</div>

				<k-expand-transition><div v-if="rd.CFAssociations.total_issue_count > 0 && rd.CFAssociations.section_showing">
					<div v-for="(item, identifier, index) in rd.CFAssociations.items" :key="identifier" class="k-bpr-item" :class="item==last_clicked_item?'k-bpr-item-last-clicked':''">
						<div class="d-flex align-center">
							<b>{{(index+1)}}.  </b>
							<div class="k-bpr-item-display" v-html="item.item_display"></div>
							<k-btn x-small text class="k-tight-btn ml-1 k-bp-hide-from-print" color="primary" @click="view_json(item)"><k-icon x-small class="mr-1">fas fa-code</k-icon> JSON</k-btn>
							<k-spacer/>
						</div>
						<ul class="k-bpr-section-issues">
							<li v-for="(count, issue_code) in item.issue_counts" :key="issue_code" v-html="issue_html(issue_code, -1, 'A')"></li>
						</ul>
					</div>
				</div></k-expand-transition>
			</div>

			<!-- <div class="k-bpr-section">
				<div class="k-bpr-section-title">Other Best Practices</div>
				<div><k-icon color="green darken-3" class="mr-2">fas fa-square-check</k-icon>Good</div>
				<div><k-icon color="red darken-3" class="mr-2">fas fa-square-xmark</k-icon>Bad</div>
			</div> -->
		</div>
	</k-card-text>

	<k-dialog v-if="jd_item" v-model="jd_item_showing" max-width="1000" scrollable>
		<k-card class="deep-purple lighten-5">
			<k-card-title style="font-size:18px">
				<b>{{jd_item.entity_type}}:</b>
				<span class="ml-2">{{jd_item.identifier}}</span>
				<k-spacer/>
				<k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="json_version_showing" mandatory>
					<k-btn small light value="json_display">  Source JSON  </k-btn>
					<k-btn small light value="sanitized_json_display">Normalized JSON</k-btn>
				</k-btn-toggle>
				<k-spacer/>
				<k-btn color="secondary" dark small fab accessible-label="Close JSON viewer" @click="jd_item=null;jd_item_showing=false"><k-icon>fas fa-xmark</k-icon></k-btn>
			</k-card-title>
			<k-card-text class="mt-3">
				<div class="k-ctmi-json white" style="border:1px solid #999;">{{jd_item[json_version_showing]}}</div>
			</k-card-text>
		</k-card>
	</k-dialog>
</k-card></k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		framework_record: { type: Object, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		sanitized_json: null,
		v1p1_json: null,
		rd: null,
		jd_item: null,
		jd_item_showing: true,
		last_clicked_item: null,
		json_version_showing: 'json_display',
		bp_issue_data: null,
	}},
	computed: {
		...mapState(['user_info']),
		...mapGetters([]),
		framework_identifier() { return this.framework_record.lsdoc_identifier },
		show_refresh_btn() {
			// only show the refresh button to cglt employees
			return this.user_info.email.includes('commongoodlt.com')
		},
		show_open_framework_btn() {
			return true
		},
	},
	watch: {
	},
	created() {
		vapp.bp_report_component = this
	},
	mounted() {
		this.load_bp_data()
	},
	methods: {
		async load_bp_data(flag) {
			// if framework record isn't fully loaded, load it
			await U.load_framework_and_cfo(this.framework_identifier)

			// then load the bp_data
			let payload = {
				user_id: this.user_info.user_id,
				framework_identifier: this.framework_identifier,
			}

			// we don't initially force_update when the report is first loaded, but allow some users to request a forced update
			if (flag == 'force_update') payload.force_update = 'yes'

			U.loading_start()
			let result = await U.ajax_promise('get_best_practices_data', payload)
			U.loading_stop()
			if (result.status != 'ok') {
				vapp.ping()		// call ping to check if the session is expired
				console.log('Error retrieving best practices report data')
				return
			}

			// save bp_issue_data
			this.bp_issue_data = result.bp_issue_data

			// if we didn't get report data...
			if (empty(result.report)) {
				// if flag wasn't already 'force_update', try again
				if (flag != 'force_update') {
					this.load_bp_data('force_update')
				} else {
					this.$alert('A best practices report could not be generated for this framework.')
				}
				return
			}

			result.report = JSON.parse(result.report)
			console.log(result.report)
			this.sanitized_json = JSON.parse(result.sanitized_json)
			// this.v1p1_json = JSON.parse(result.v1p1_json)	// not currently using this

			// save retrieved data to framework record
			this.$store.commit('set', [this.framework_record.ss_framework_data, 'bp_score', result.report.bp_score])
			this.$store.commit('set', [this.framework_record.ss_framework_data, 'bp_issue_count', result.report.bp_issue_count])
			this.$store.commit('set', [this.framework_record.ss_framework_data, 'bp_issue_type_count', result.report.bp_issue_type_count])
			this.$store.commit('set', [this.framework_record.ss_framework_data, 'item_count', this.framework_record.json.CFItems.length])

			// if the original framework's doc doesn't have caseVersion, remove it from the sanitized_json
			if (empty(this.framework_record.json.CFDocument.caseVersion)) {
				delete this.sanitized_json.CFDocument.caseVersion
			}

			let base_report_data = {
				bp_score: 0,
				total_issue_count: 0,
				issue_counts: {},
				lines: [],
			}

			let report_data = {
				summary: {
					bp_score: result.report.bp_score,
					item_type_pct: result.report.item_type_pct,
					ed_level_pct: result.report.ed_level_pct,
					sequenceNumber_pct: result.report.sequenceNumber_pct,
					bp_issue_type_count: result.report.bp_issue_type_count,
					total_issue_count: 0,
				},
				CFDocument: {},
				CFItems: {
					bp_score: 0,
					section_showing: false,
					issue_counts: {},
					total_issue_count: 0,
					item_count: 0,
					items: {},
				},
				CFAssociations: {
					bp_score: 0,
					section_showing: false,
					issue_counts: {},
					total_issue_count: 0,
					item_count: 0,
					items: {},
				},
			}
			let r

			// CFDocument issues
			r = report_data.CFDocument = object_copy(base_report_data)
			r.bp_score = result.report.CFDocument.bp_score
			r.section_showing = true
			r.entity_type = 'CFDocument'
			r.identifier = this.framework_identifier
			r.json = this.framework_record.json.CFDocument
			r.json_display = JSON.stringify(r.json, null, 4)
			r.sanitized_json = this.sanitized_json.CFDocument
			r.sanitized_json_display = JSON.stringify(r.sanitized_json, null, 4)

			for (let field in result.report.CFDocument) {
				this.add_issue(r, field, result.report.CFDocument[field])
			}
			this.sort_issue_counts(report_data.CFDocument)
			report_data.summary.total_issue_count += r.total_issue_count

			// CFItem issues
			report_data.CFItems.bp_score = result.report.CFItems.bp_score
			for (let id in result.report.CFItems) {
				if (id == 'level_issue_count') continue
				if (id == 'bp_score') continue

				r = object_copy(base_report_data)
				r.entity_type = 'CFItem'
				r.identifier = id

				r.json = this.framework_record.json.CFItems.find(x=>x.identifier == id)
				if (r.json) {
					r.json_display = JSON.stringify(r.json, null, 4)
					r.item_display = U.generate_cfassociation_node_uri_title(r.json, 50, true, true)
				} else {
					r.item_display = id
					r.json_display = 'No JSON found.'
				}

				r.sanitized_json = this.sanitized_json.CFItems.find(x=>x.identifier == id)
				if (r.sanitized_json) {
					r.sanitized_json_display = JSON.stringify(r.sanitized_json, null, 4)
				} else {
					r.sanitized_json = 'No JSON found.'
				}

				for (let field in result.report.CFItems[id]) {
					this.add_issue(r, field, result.report.CFItems[id][field], report_data.CFItems)
				}
				this.sort_issue_counts(r)

				report_data.CFItems.item_count += 1
				report_data.CFItems.total_issue_count += r.total_issue_count
				report_data.CFItems.items[id] = r

				report_data.summary.total_issue_count += r.total_issue_count
			}
			this.sort_issue_counts(report_data.CFItems)

			// CFAssociation issues
			report_data.CFAssociations.bp_score = result.report.CFAssociations.bp_score
			for (let id in result.report.CFAssociations) {
				if (id == 'level_issue_count') continue
				if (id == 'bp_score') continue

				r = object_copy(base_report_data)
				r.entity_type = 'CFAssociation'
				r.identifier = id
				r.json = this.framework_record.json.CFAssociations.find(x=>x.identifier == id)

				if (r.json) {
					r.json_display = JSON.stringify(r.json, null, 4)
					let o = (r.json.originNodeURI.title || '???').substr(0, 30).replace(/</g, '&lt;')
					let d = (r.json.destinationNodeURI.title || '???').substr(0, 30).replace(/</g, '&lt;')
					r.item_display = `${o} → ${r.json.associationType} → ${d}`

				} else {
					r.item_display = id
					r.json_display = 'No JSON found.'
				}

				r.sanitized_json = this.sanitized_json.CFAssociations.find(x=>x.identifier == id)
				if (r.sanitized_json) {
					r.sanitized_json_display = JSON.stringify(r.sanitized_json, null, 4)
				} else {
					r.sanitized_json = 'No JSON found.'
				}

				for (let field in result.report.CFAssociations[id]) {
					this.add_issue(r, field, result.report.CFAssociations[id][field], report_data.CFAssociations)
				}
				this.sort_issue_counts(r)

				report_data.CFAssociations.item_count += 1
				report_data.CFAssociations.total_issue_count += r.total_issue_count
				report_data.CFAssociations.items[id] = r

				report_data.summary.total_issue_count += r.total_issue_count
			}
			this.sort_issue_counts(report_data.CFAssociations)

			this.rd = report_data
		},

		add_issue(r, field, o, section_report) {
			if (field == 'level_issue_count') return
			if (field == 'bp_score') return

			// if field is, e.g., 'orphaned_item', o should be a number (probably 1)
			if (!isNaN(o*1)) {
				r.total_issue_count += 1
				r.issue_counts[field] = 1
				let d = this.bp_issue_data[field]?.description ?? field
				r.lines.push(d)

				// add to section_report.issue_counts
				if (section_report) {
					if (empty(section_report.issue_counts[field])) {
						section_report.issue_counts[field] = 0
					}
					section_report.issue_counts[field] += 1
				}
				return
			}

			// else field should be, e.g., 'notes', and o could be, e.g.: {markdown_replacements: 1}
			for (let key in o) {
				r.total_issue_count += o[key]
				r.issue_counts[key] = o[key]
				let d = this.bp_issue_data[key]?.description ?? key
				r.lines.push(`${field}: ${d}`)

				// add to section_report.issue_counts
				if (section_report) {
					if (empty(section_report.issue_counts[key])) {
						section_report.issue_counts[key] = 0
					}
					section_report.issue_counts[key] += 1
				}
			}
		},

		sort_issue_counts(rd) {
			// sort issue_counts so that highest-severity items come first
			let arr = Object.keys(rd.issue_counts)
			arr.sort((a,b) => {
				a = this.bp_issue_data[a]?.level ?? 1
				b = this.bp_issue_data[b]?.level ?? 1
				return b-a
			})

			let o = {}
			for (let key of arr) {
				o[key] = rd.issue_counts[key]
			}
			rd.issue_counts = o
		},

		severity_indicator(severity_level) {
			return vapp.framework_list_component.bp_severity_indicator(severity_level)
		},

		issue_html(issue_code, count, code_number_prefix) {
			// console.warn(issue_code, count)
			let bpid = this.bp_issue_data[issue_code]	// bp_issue_data

			let code_number = bpid.code
			if (code_number[0] == 'T') {
				code_number = code_number_prefix + code_number
			}

			let s = ''

			s += this.severity_indicator(bpid.level)
			s += `<span class="ml-2 k-bp-issue-code-number">${code_number}</span>`
			if (count > -1) {
				s += `<span class="ml-2">${count} ${U.ps('issue', count)}:</span>`
			}
			s += `<span class="ml-2">${bpid.description}</span>`

			// customize a few descriptions/long_descriptions; these are only applicable for CFDocument-level issues
			let description = bpid.description
			let long_description = bpid.long_description ?? ''
			if (issue_code == 'insufficient_itemTypes') {
				bpid.description = description.replace(/XXX/, this.rd.summary.item_type_pct)
				long_description = long_description.replace(/XXX/, this.rd.summary.item_type_pct)
			}
			if (issue_code == 'insufficient_educationLevels') {
				bpid.description = description.replace(/XXX/, this.rd.summary.ed_level_pct)
				long_description = long_description.replace(/XXX/, this.rd.summary.ed_level_pct)
			}
			if (issue_code == 'sequenceNumber_missing') {
				bpid.description = description.replace(/XXX/, this.rd.summary.sequenceNumber_pct)
				long_description = long_description.replace(/XXX/, this.rd.summary.sequenceNumber_pct)
			}

			// add link to show more if we have a long_description
			if (!empty(long_description)) {
				let uuid = U.new_uuid()
				s += `<a class="k-bp-more-info-link k-bp-hide-from-print" href="javascript:vapp.bp_report_component.toggle_long_description('${uuid}')">MORE INFO</a>`
				s += `<div id="${uuid}" class="k-bp-more-info-description" style="display:none">${long_description}</div>`
			}

			return s
		},

		toggle_long_description(uuid) {
			let jq = $('#'+uuid)
			if (jq.css('display') == 'none') jq.css('display', 'block')
			else jq.css('display', 'none')
		},

		section_icon(section) {
			if (section.section_showing) return 'fas fa-caret-up'
			return 'fas fa-caret-right'
		},

		view_json(rd) {
			this.jd_item_showing = true
			this.jd_item = rd
			this.last_clicked_item = rd
		},

		view_framework(rd) {
			let data = {
				framework_identifier: this.framework_identifier,
				item_identifier: rd.identifier,
				allow_framework_switching: false	// don't allow framework switching here
			}
			// data.selected_items = [rd.identifier]
			
			let show_data = { 
				// fn called when a new framework is chosen by the user (also called as soon as the iframe loads)
				// framework_chosen_callback_fn: (framework_identifier)=> {
				// },

				// fn called when embedded satchel is hidden
				embed_hide_callback_fn: (arg)=>{ 
				},

				// fn called continuously while embedded satchel is open; if it returns true, embedded satchel will be closed
				hide_fn: ()=>{ 
				},

				small_frame_top: 0,
				// small_frame_width: this.satchel_embed_width
			}
			// console.warn('show: show_alignment')
			vapp.$refs.satchel.execute('show', show_data).then(()=>{
				vapp.$refs.satchel.execute('load_framework', data).then(()=>{
				})
			})

		},

		print_report() {
			const divContents = document.getElementById('k-bpr-text').innerHTML

			// Collect all stylesheets and inline styles
			const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
				.map(node => node.outerHTML)
				.join('')

			// Open a new print window
			// const printWindow = window.open('', '', 'height=800,width=600');
			const printWindow = window.open()
			printWindow.document.write('<html><head><title>Print</title>')
			printWindow.document.write(styles) // inject styles here
			printWindow.document.write('</head><body>')
			printWindow.document.write(divContents)
			printWindow.document.write('</body></html>')

			printWindow.document.close()
			printWindow.focus()

			printWindow.print()
			printWindow.close()
		},

		copy_report_link() {
			let url = `${window.location.origin}/${this.framework_record.lsdoc_identifier}?bp`
			U.copy_to_clipboard(url)
			this.$inform('Link copied to clipboard')
		},

		open_framework() {
			// if the framework isn't already open, open it
			if (vapp.case_tree_component?.lsdoc_identifier != this.framework_record.lsdoc_identifier) {
				vapp.framework_list_component.view_framework(this.framework_record.lsdoc_identifier)
			}
			// close the BP dialog
			this.$emit('dialog_cancel')
		},

	}
}

</script>

<style lang="scss">
.k-bpr-intro {
	background-color:$v-deep-purple-lighten-5;
	border-radius:8px;
	margin:16px 32px 12px 32px;
	padding:12px;
	width:820px;
}

.k-bpr-section {
	margin:16px 0 0 60px;
}

.k-bpr-section-title {
	font-weight:bold;
	font-size:18px;
	margin-left:-60px;
	display:flex;
	align-items: center;
	margin-bottom:4px;
}

.k-bpr-section-issues {
	margin-left:-28px;
	margin-bottom:8px;
	li {
		list-style-type: none;
		padding-left: 0px;
		margin-left: 32px;
		text-indent: -32px;
	}
}

// note that .k-bp-severity-indicator is in FrameworkList

.k-bpr-section-issue-counts {
	font-weight:normal;
	font-size:15px;
	margin-left:12px;
	color:$v-red-darken-3;
}

.k-bpr-item {
	margin-top:-1px;
	border-top:1px solid $v-amber-lighten-3;
	border-bottom:1px solid $v-amber-lighten-3;
	padding:4px 8px;
	// margin-bottom:4px;
	cursor:pointer;
}

.k-bpr-item-display {
	display:inline-block;
	width:560px;
	max-width:560px;
	white-space:nowrap;
	overflow:auto;
}

.k-bpr-item:hover, .k-bpr-item-last-clicked {
	background-color:$v-amber-lighten-5;
}

.k-bp-issue-code-number {
	font-family:monospace;
	font-size:14px;
	font-weight:bold;
}

.k-bp-more-info-link {
	color:$v-deep-purple!important;
	font-weight:bold;
	font-size:12px;
	margin-left:6px;
}

.k-bp-more-info-description {
	font-size:14px;
	line-height:18px;
	max-width:600px;
	margin-bottom:12px;
	margin-left:8px;
	text-indent:0px;
}

.k-bpr-print-header { display:none; }

@media print {
	.k-bp-hide-from-print { display:none; }
	.k-bpr-print-header { display: block; }
	.py-1 { padding-top:4px; padding-bottom:4px; }
	.ml-2 { margin-left:8px; }
	.k-bpr-section-issue-counts { color:#000!important; }

	.k-bpr-section-issues {
		li {
			list-style-type: disc;
		}
	}

	/* Set page margins (tweak as needed) */
	@page { margin: 12mm; }
}

</style>
